import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  MenuItem,
  TextField,
  Typography,
  Switch,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import dayjs from 'dayjs';
import { setAllFarmDevices, setFarmDevice } from '../../../../actions/farmSectorActions';
import CustomButton from '../../../../components/General/CustomButton';
import CustomConfirmationModal from '../../../../components/General/CustomConfirmationModal';
import CustomHeader from '../../../../components/General/CustomHeader';
import Page from '../../../../components/Layout/Page';
import useFetchApi from '../../../../hooks/useFetchApi';
import apiClient from '../../../../requests/api/apiClient';
import { GetNodeByIdResponse, PutNodeSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { FetchError } from '../../../types';
import ImagesContainer from './component/ImagesContainer';
import useStyles from './styles';

const telemetryTypeOptions: string[] = ['sn', 'wiseconn'];

const NodesDetail = () => {
  const classes = useStyles();
  const { id: nodeId } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [refreshDevice, setRefreshDevice] = useState(false);
  const { allFarmDevices } = useSelector((state: ReduxState) => state.farmSector);
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);

  const { data: fetchData, loading: isLoading } = useFetchApi<GetNodeByIdResponse>({
    modelEndpoint: 'nodesDetail',
    modelName: 'nodes',
    id: nodeId,
    dependencies: [refreshDevice],
  });

  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const [isSendingTicket, setIsSendingTicket] = useState(false);
  const [editableName, setEditableName] = useState('');
  const [editableSoftlandBrand, setEditableSoftlandBrand] = useState('');
  const [editableSoftlandModel, setEditableSoftlandModel] = useState('');
  const [editableSector, setEditableSector] = useState('');
  const [editableTelemetryType, setEditableTelemetryType] = useState('wiseconn');
  const [editableWiseconnId, setEditableWiseconnId] = useState('');
  const [editableIsGatewayNode, setEditableIsGatewayNode] = useState(false);
  const [editableModemId, setEditableModemId] = useState('');
  const [editableNetworkNumber, setEditableNetworkNumber] = useState('');
  const [editableMac, setEditableMac] = useState('');
  const [editableComment, setEditableComment] = useState('');
  const [editableActive, setEditableActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [nodeModels, setNodeModels] = useState<{ models: string[], brands: string[] }>(
    { models: [], brands: [] },
  );

  useEffect(() => {
    if (fetchData?.node) {
      setEditableName(fetchData.node.name || '');
      setEditableSoftlandBrand(fetchData.node.softlandBrand || '');
      setEditableSoftlandModel(fetchData.node.softlandModel || '');
      // eslint-disable-next-line no-underscore-dangle
      setEditableSector(fetchData.node.sector?.id || (fetchData.node.sector as any)?._id || '');
      setEditableTelemetryType(fetchData.node.telemetryType || 'wiseconn');
      setEditableWiseconnId(fetchData.node.wiseconnId || '');
      setEditableIsGatewayNode(fetchData.node.isGatewayNode || false);
      setEditableModemId(fetchData.node.modemId || '');
      setEditableNetworkNumber(fetchData.node.networkNumber?.toString() || '');
      setEditableMac(fetchData.node.mac || '');
      setEditableComment(fetchData.node.comment || '');
      setEditableActive(fetchData.node.active ?? true);
    }
  }, [fetchData]);

  useEffect(() => {
    const getDeviceModels = async () => {
      try {
        const models = await apiClient.devices.softlandGetDeviceModelsList();
        setNodeModels({
          models: models.data?.nodeModels ?? [],
          brands: models.data?.nodeBrands ?? [],
        });
      } catch (error) {
        console.error('Error fetching node models:', error);
      }
    };
    getDeviceModels();
  }, []);

  const handleSaveChanges = async (overrides: Partial<PutNodeSchema> = {}) => {
    try {
      setIsSaving(true);
      const updateData: PutNodeSchema = {
        id: nodeId,
        ...overrides,
      };

      const node = fetchData?.node;

      const assignIfChanged = (field: keyof PutNodeSchema, value: any, originalValue: any) => {
        if (value !== originalValue && value !== '') {
          (updateData as any)[field] = value;
        }
      };

      assignIfChanged('name', editableName, node?.name);
      assignIfChanged('softlandBrand', editableSoftlandBrand, node?.softlandBrand);
      assignIfChanged('softlandModel', editableSoftlandModel, node?.softlandModel);
      // eslint-disable-next-line no-underscore-dangle
      const originalSector = node?.sector?.id || (node?.sector as any)?._id || '';
      if (editableSector !== originalSector && editableSector !== '') {
        updateData.sector = editableSector as any;
      }
      assignIfChanged('telemetryType', editableTelemetryType, node?.telemetryType);
      assignIfChanged('wiseconnId', editableWiseconnId, node?.wiseconnId);
      assignIfChanged('isGatewayNode', editableIsGatewayNode, node?.isGatewayNode);
      assignIfChanged('modemId', editableModemId, node?.modemId);

      const parsedNetworkNumber = editableNetworkNumber ? parseInt(
        editableNetworkNumber, 10,
      ) : undefined;
      if (parsedNetworkNumber !== node?.networkNumber) {
        updateData.networkNumber = parsedNetworkNumber;
      }

      assignIfChanged('mac', editableMac, node?.mac);
      assignIfChanged('comment', editableComment, node?.comment);
      assignIfChanged('active', editableActive, node?.active);

      if (Object.keys(updateData).length <= 1 && !overrides.active) {
        // Only id is present (or active was already processed)
        enqueueSnackbar('No hay cambios para guardar', { variant: 'info' });
        return;
      }

      await apiClient.nodes.nodesUpdate(updateData, nodeId);
      enqueueSnackbar('Cambios guardados exitosamente', { variant: 'success' });
      setRefreshDevice((prev) => !prev);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendSupportTicket = async () => {
    try {
      setIsSendingTicket(true);
      const sectorName = selectedFarm?.sectors?.find((s) => s.id === editableSector)?.name || '';
      await apiClient.supportTicket.send({
        accountName: selectedAccount?.name || '',
        farmName: selectedFarm?.name || '',
        deviceName: fetchData?.node?.name || nodeId,
        deviceType: 'Nodo',
        sectorName,
        senderName: `${currentUser?.name || ''} ${currentUser?.lastname || ''}`.trim(),
        senderEmail: currentUser?.email || '',
      });
      enqueueSnackbar('Ticket enviado a soporte exitosamente', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error al enviar el ticket de soporte', { variant: 'error' });
    } finally {
      setIsSendingTicket(false);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await apiClient.nodes.nodesDelete(nodeId);
      enqueueSnackbar('Nodo eliminado con éxito', { variant: 'success' });
      setDeleteModalOpen(false);
      const allFarmDevicesFiltered = allFarmDevices.filter((node) => node.id !== nodeId);
      dispatch(setAllFarmDevices(allFarmDevicesFiltered));
      dispatch(setFarmDevice(''));
      history.push('/layout');
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Operative': return `${classes.statusBadge} ${classes.operativeStatus}`;
      case 'Offline': return `${classes.statusBadge} ${classes.offlineStatus}`;
      case 'Repair': return `${classes.statusBadge} ${classes.repairStatus}`;
      default: return classes.statusBadge;
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case 'Operative': return 'Operativo';
      case 'Offline': return 'Fuera de línea';
      case 'Repair': return 'Reparación';
      default: return status;
    }
  };

  if (isLoading && !fetchData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Page className={classes.root} title="Detalle Nodo">
      <Container maxWidth={false} className={classes.containerContainer}>
        <CustomHeader
          title={`${selectedFarm?.name || ''} / ${fetchData?.node?.name || ''}`}
          showBackButton={true}
        />

        <Box display="flex" flexDirection="column" gap={3} mt={2}>
          {/* Información Section */}
          <Card className={classes.infoCard}>
            <CardContent>
              <Typography variant="h4" gutterBottom>Información</Typography>
              <Box className={classes.infoRow} mt={2}>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Nombre nodo"
                    value={editableName}
                    onChange={(e) => setEditableName(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Marca Softland"
                    value={editableSoftlandBrand}
                    onChange={(e) => setEditableSoftlandBrand(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {nodeModels.brands.map((brand) => (
                      <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Modelo Softland"
                    value={editableSoftlandModel}
                    onChange={(e) => setEditableSoftlandModel(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {nodeModels.models.map((model) => (
                      <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Sector"
                    value={editableSector}
                    onChange={(e) => setEditableSector(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {selectedFarm?.sectors?.map((sector) => (
                      <MenuItem key={sector.id} value={sector.id}>
                        {sector.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Tipo telemetría"
                    value={editableTelemetryType}
                    onChange={(e) => setEditableTelemetryType(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {telemetryTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === 'sn' ? 'Helius' : 'Wiseconn'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Wiseconn ID"
                    value={editableWiseconnId}
                    onChange={(e) => setEditableWiseconnId(e.target.value)}
                    fullWidth
                    variant="standard"
                    disabled={editableTelemetryType !== 'wiseconn'}
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Es nodo gateway"
                    value={editableIsGatewayNode ? 'si' : 'no'}
                    onChange={(e) => setEditableIsGatewayNode(e.target.value === 'si')}
                    fullWidth
                    variant="standard"
                  >
                    <MenuItem value="si">Si</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Modem ID"
                    value={editableModemId}
                    onChange={(e) => setEditableModemId(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Número de red"
                    type="number"
                    value={editableNetworkNumber}
                    onChange={(e) => setEditableNetworkNumber(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Dirección MAC"
                    value={editableMac}
                    onChange={(e) => setEditableMac(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem} display="flex" alignItems="center" pt={2} gap={1}>
                  <Typography variant="body2" color="textSecondary">Activo:</Typography>
                  <Switch
                    checked={editableActive}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setEditableActive(newValue);
                      handleSaveChanges({ active: newValue });
                    }}
                    color="primary"
                  />
                </Box>
                <Box className={classes.infoItem} display="flex"
                  alignItems="center" pt={2} flexDirection="row" gap={1}>
                  <Typography variant="body2" color="textSecondary" display="flex"
                    flexDirection="column" alignItems="start" gap={1}>
                    <Box>
                      Estado:
                      <span className={getStatusStyle(fetchData?.node?.status || 'Offline')}>
                        {translateStatus(fetchData?.node?.status || 'Offline')}
                      </span>
                    </Box>
                    {' '}
                    Última actividad: {fetchData?.node?.lastActivity
                      ? dayjs(fetchData?.node?.lastActivity).format('DD/MM/YYYY HH:mm') : '-'}
                  </Typography>
                </Box>
              </Box>
              <Box mt={3}>
                <Divider />
                <Box mt={2}>
                  <TextField
                    label="Comentario"
                    value={editableComment}
                    onChange={(e) => setEditableComment(e.target.value)}
                    fullWidth
                    variant="standard"
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={3} gap={2} flexWrap="wrap">
                {fetchData?.node?.status === 'Offline' && (
                  <CustomButton
                    buttonText="Reportar equipo offline"
                    color="blue"
                    onClick={handleSendSupportTicket}
                    disabled={isSendingTicket}
                    setLoadingSpinnerWhenDisabled={true}
                    style={{ width: '250px' }}
                  />
                )}
                <CustomButton
                  buttonText="Guardar cambios"
                  color="blue"
                  onClick={() => handleSaveChanges()}
                  disabled={isSaving}
                  setLoadingSpinnerWhenDisabled={true}
                  style={{ width: '250px' }}
                />
                <CustomButton
                  buttonText="Eliminar Nodo"
                  color="red"
                  onClick={() => setDeleteModalOpen(true)}
                  style={{ width: '250px' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Imagenes Section */}
          <Card className={classes.imagesCard}>
            <CardContent>
              {nodeId ? (
                <ImagesContainer deviceId={nodeId} />
              ) : (
                <Typography>Cargando imágenes...</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>

      <CustomConfirmationModal
        open={deleteModalOpen}
        title="Eliminar Nodo"
        message="¿Estás seguro de que quieres eliminar este nodo?"
        onConfirm={handleDeleteDevice}
        onCancel={() => setDeleteModalOpen(false)}
        confirmButtonColor="delete"
        confirmText="Eliminar"
      />
    </Page>
  );
};

export default NodesDetail;
